import { RefObject, useEffect, useRef, useState } from "react";
import { loadGridHeight, saveGridHeight } from "./state";

const RESIZE_GRIP_HIT_AREA = 20;
const RESIZE_PERSIST_DEBOUNCE_MS = 400;
const RESIZE_TOOLTIP = "Drag the bottom-right corner to resize";

/**
 * HHMCOVID-6327 AC1/AC2 — tracks a manual vertical resize of `bDivRef`'s
 * element (the React Monitor Dashboard grid body) and persists it.
 *
 * The element must carry `resize: vertical; overflow: auto` (or similar) —
 * this hook only detects when a resulting resize was user-driven, and
 * restores a persisted value on mount.
 *
 * `resize: vertical` draws its handle in roughly the bottom-right ~20px
 * corner with no DOM event for "a drag started/ended", so this tracks it via
 * mousedown hit-testing that corner combined with a ResizeObserver. Gating on
 * an actual corner mousedown (rather than every ResizeObserver firing) stops
 * incidental size changes — window resize, zoom, a row-count reflow — from
 * being mistaken for a manual resize.
 *
 * Deliberately does NOT expose an "is dragging" flag: doing so previously
 * meant a mousedown-triggered re-render mutated this element's CSS at the
 * exact instant the browser started its own native resize gesture, causing a
 * visible jump. `isResizingRef` stays an internal, non-rendering ref for
 * exactly that reason — it only gates which ResizeObserver firings count as
 * a real drag, and never triggers a re-render by itself.
 */
export function useManualGridResize(bDivRef: RefObject<HTMLDivElement | null>, stateKey: string): number | null {
  const [manualHeight, setManualHeight] = useState<number | null>(() => loadGridHeight(stateKey));
  const isResizingRef = useRef(false);

  useEffect(() => {
    const bDiv = bDivRef.current;
    if (!bDiv) {
      return;
    }

    // Declared once per effect run (not per mousedown) so the cleanup below
    // can always remove it — a drag started right before this component
    // unmounts would otherwise leave a dangling `mouseup` listener on
    // `window` until the user's next unrelated mouseup anywhere on the page.
    const onMouseUp = () => {
      isResizingRef.current = false;
    };

    // `resize: vertical`'s grip has no DOM node of its own — this hit-test
    // is the only way to know the cursor is over it, for both starting a
    // drag (onMouseDown) and scoping the discoverability tooltip
    // (onMouseMove) to just that corner, not the whole scrollable body.
    const isInResizeCorner = (event: MouseEvent) => {
      const rect = bDiv.getBoundingClientRect();
      return rect.right - event.clientX <= RESIZE_GRIP_HIT_AREA && rect.bottom - event.clientY <= RESIZE_GRIP_HIT_AREA;
    };

    const onMouseDown = (event: MouseEvent) => {
      if (!isInResizeCorner(event)) {
        return;
      }
      isResizingRef.current = true;
      window.addEventListener("mouseup", onMouseUp);
    };

    // The native `title` attribute tooltips the whole element it's set on —
    // toggling it based on cursor position is what scopes it to just the
    // resize corner instead of anywhere in the grid body.
    const onMouseMove = (event: MouseEvent) => {
      bDiv.title = isInResizeCorner(event) ? RESIZE_TOOLTIP : "";
    };
    const onMouseLeave = () => {
      bDiv.title = "";
    };

    bDiv.addEventListener("mousedown", onMouseDown);
    bDiv.addEventListener("mousemove", onMouseMove);
    bDiv.addEventListener("mouseleave", onMouseLeave);
    return () => {
      bDiv.removeEventListener("mousedown", onMouseDown);
      bDiv.removeEventListener("mousemove", onMouseMove);
      bDiv.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [bDivRef]);

  // While a drag (per isResizingRef above) is in progress, echoes the box's
  // own current size back as an explicit `height` (a visual no-op, since
  // the box is already that size) and persists it, debounced so a single
  // drag gesture (many intermediate sizes) writes to session storage once.
  useEffect(() => {
    const bDiv = bDivRef.current;
    if (!bDiv || typeof ResizeObserver === "undefined") {
      return;
    }

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const observer = new ResizeObserver(() => {
      if (!isResizingRef.current) {
        return;
      }

      // `bDiv` is explicitly `box-sizing: border-box` (see Grid.tsx), and
      // getBoundingClientRect() always reports the border-box size regardless
      // of box-sizing — unlike ResizeObserver's own `contentRect`, which is
      // always content-box and would drift from the CSS `height` this echoes
      // back if the element ever gained padding/border.
      const newHeight = Math.round(bDiv.getBoundingClientRect().height);
      if (!Number.isFinite(newHeight) || newHeight <= 0) {
        return;
      }

      setManualHeight(newHeight);

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        saveGridHeight(stateKey, newHeight);
      }, RESIZE_PERSIST_DEBOUNCE_MS);
    });

    observer.observe(bDiv);

    return () => {
      observer.disconnect();
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [bDivRef, stateKey]);

  return manualHeight;
}
